import React, { useRef, useState, useEffect } from 'react';

interface TimeInput24Props {
  value: string; // "HH:MM:SS"
  onChange: (value: string) => void;
  className?: string;
}

const clamp = (n: number, min: number, max: number) => Math.min(max, Math.max(min, n));
const pad = (n: number) => String(n).padStart(2, '0');

const TimeInput24: React.FC<TimeInput24Props> = ({ value, onChange, className }) => {
  const parts = value ? value.split(':') : ['00', '00', '00'];
  const committed = {
    hh: parts[0] ?? '00',
    mm: parts[1] ?? '00',
    ss: parts[2] ?? '00',
  };

  const [draft, setDraft] = useState<{ hh: string; mm: string; ss: string }>(committed);

  const hhRef = useRef<HTMLInputElement>(null);
  const mmRef = useRef<HTMLInputElement>(null);
  const ssRef = useRef<HTMLInputElement>(null);

  const nextRef: Record<'hh' | 'mm' | 'ss', React.RefObject<HTMLInputElement> | null> = {
    hh: mmRef,
    mm: ssRef,
    ss: null,
  };

  // Keep draft in sync when the parent changes value externally
  useEffect(() => {
    setDraft(committed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const handleChange = (seg: 'hh' | 'mm' | 'ss', raw: string) => {
    const digits = raw.replace(/\D/g, '');
    setDraft((prev) => ({ ...prev, [seg]: digits }));

    // Auto-advance to next segment once 2 digits are entered
    if (digits.length === 2) {
      const next = nextRef[seg];
      if (next?.current) {
        next.current.focus();
        next.current.select();
      }
    }
  };

  const commit = (seg: 'hh' | 'mm' | 'ss', raw: string) => {
    const num = parseInt(raw, 10);
    const max = seg === 'hh' ? 23 : 59;
    const clamped = isNaN(num) ? 0 : clamp(num, 0, max);
    const padded = pad(clamped);
    const next = { ...draft, [seg]: padded };
    setDraft(next);
    onChange(`${next.hh}:${next.mm}:${next.ss}`);
  };

  const segClass =
    'w-10 text-center bg-transparent outline-none text-base font-mono font-semibold text-gray-900 focus:text-blue-600';

  return (
    <div
      className={`flex items-center border border-gray-300 rounded-lg px-3 bg-white focus-within:ring-2 focus-within:ring-blue-400 focus-within:border-blue-400 ${className ?? ''}`}
      style={{ height: '38px' }}
    >
      <input
        ref={hhRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={draft.hh}
        onChange={(e) => handleChange('hh', e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={(e) => commit('hh', e.target.value)}
        placeholder="HH"
        aria-label="Hours"
        className={segClass}
      />
      <span className="font-mono text-gray-400 select-none">:</span>
      <input
        ref={mmRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={draft.mm}
        onChange={(e) => handleChange('mm', e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={(e) => commit('mm', e.target.value)}
        placeholder="MM"
        aria-label="Minutes"
        className={segClass}
      />
      <span className="font-mono text-gray-400 select-none">:</span>
      <input
        ref={ssRef}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        maxLength={2}
        value={draft.ss}
        onChange={(e) => handleChange('ss', e.target.value)}
        onFocus={(e) => e.target.select()}
        onBlur={(e) => commit('ss', e.target.value)}
        placeholder="SS"
        aria-label="Seconds"
        className={segClass}
      />
    </div>
  );
};

export default TimeInput24;
